import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FileText, BookOpen, HelpCircle, ArrowRight, Calendar,
  ChevronDown
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CTABanner } from '../components/shared/CTABanner';
import { SectionHeader } from '../components/shared/SectionHeader';

const articles = [
  {
    title: 'The Complete Guide to Fleet Electrification',
    description: 'Everything you need to know about transitioning your fleet to electric vehicles, from planning to deployment.',
    category: 'Guide',
    readTime: '12 min read',
    date: 'Dec 2024'
  },
  {
    title: 'Understanding Charging-as-a-Service (CaaS)',
    description: 'How CaaS models work and why they might be the right choice for your organization.',
    category: 'Article',
    readTime: '8 min read',
    date: 'Nov 2024'
  },
  {
    title: 'EV Infrastructure: CapEx vs OpEx',
    description: 'A detailed comparison of traditional capital investment versus operational expense models for EV charging.',
    category: 'Whitepaper',
    readTime: '15 min read',
    date: 'Oct 2024'
  },
  {
    title: 'Load Management Best Practices',
    description: 'Strategies for optimizing electrical load across your charging infrastructure.',
    category: 'Guide',
    readTime: '10 min read',
    date: 'Sep 2024'
  },
  {
    title: 'Utility Coordination: What to Expect',
    description: 'Navigating the utility interconnection process for large-scale EV deployments.',
    category: 'Article',
    readTime: '6 min read',
    date: 'Aug 2024'
  },
  {
    title: 'Federal and State EV Incentives in 2024',
    description: 'A comprehensive overview of available incentives and how to take advantage of them.',
    category: 'Guide',
    readTime: '9 min read',
    date: 'Jul 2024'
  }
];

const faqs = [
  {
    question: 'What is the typical timeline for an EV charging installation?',
    answer: 'Timeline varies based on project scope and complexity. Simple Level 2 installations can take 4-8 weeks, while large-scale DC fast charging deployments may require 3-6 months including engineering, permitting, and utility coordination.'
  },
  {
    question: 'How does Charging-as-a-Service (CaaS) work?',
    answer: 'With CaaS, Skytec EV handles the entire lifecycle of your charging infrastructure — from design and installation to ongoing operations and maintenance. You pay a predictable monthly fee with no upfront capital investment, and we guarantee uptime and performance.'
  },
  {
    question: 'What types of chargers do you install?',
    answer: 'We deploy a full range of charging solutions including Level 2 AC chargers (7-22kW), DC fast chargers (50-150kW), and high-power DC chargers (150-350kW). We are hardware-agnostic and can recommend the best equipment for your specific use case.'
  },
  {
    question: 'Do you handle utility coordination and permitting?',
    answer: 'Yes. Our team manages the complete permitting process and coordinates directly with utilities on service upgrades, interconnection agreements, and demand charges. This is often the most complex part of EV infrastructure deployment, and we have extensive experience navigating it.'
  },
  {
    question: 'What ongoing support do you provide after installation?',
    answer: 'We offer comprehensive managed services including 24/7 remote monitoring, predictive maintenance, emergency repairs, software updates, and performance analytics. Our goal is to maximize uptime and minimize total cost of ownership.'
  },
  {
    question: 'Can you integrate with our existing fleet management system?',
    answer: 'Yes. Our platform supports OCPP 1.6 and 2.0 protocols and can integrate with most fleet management and telematics systems. We also provide APIs for custom integrations.'
  },
  {
    question: 'What areas do you serve?',
    answer: 'Skytec EV operates nationwide across all 50 states. We have regional teams positioned to support projects of any scale, from single-site deployments to multi-state rollouts.'
  },
  {
    question: 'How do you ensure reliability and uptime?',
    answer: 'Our infrastructure is designed for utility-grade reliability. We use predictive maintenance algorithms, maintain local parts inventory, and have service technicians on call. Our CaaS customers typically see 99%+ uptime.'
  }
];

const categoryIcons = {
  'Guide': BookOpen,
  'Article': FileText,
  'Whitepaper': FileText
};

export const ResourcesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', ...new Set(articles.map(a => a.category))];
  const filteredArticles = selectedCategory === 'all' 
    ? articles 
    : articles.filter(a => a.category === selectedCategory);

  return (
    <main className="pt-20" data-testid="resources-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50 circuit-pattern" data-testid="resources-hero">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A2B5] mb-4 block">
              Resources
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6">
              Guides, Articles &{' '}
              <span className="gradient-text">FAQs</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Expert insights on EV charging infrastructure, fleet electrification, and industry best practices.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="articles-section">
        <div className="container-custom">
          <SectionHeader
            overline="Knowledge Base"
            title="Articles & Guides"
          />

          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-[#00A2B5] to-[#36D890] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${category}`}
              >
                {category === 'all' ? 'All Resources' : category}
              </button>
            ))}
          </div>

          {/* Articles Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article, index) => {
              const IconComponent = categoryIcons[article.category] || FileText;
              return (
                <motion.article
                  key={article.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 border border-slate-200 card-hover cursor-pointer"
                  data-testid={`article-${index}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00A2B5]/10 to-[#36D890]/10 flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-[#00A2B5]" />
                    </div>
                    <span className="px-3 py-1 bg-slate-100 rounded-full text-xs font-semibold text-slate-600">
                      {article.category}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-3 leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                    {article.description}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {article.date}
                      </span>
                      <span>{article.readTime}</span>
                    </div>
                    <span className="inline-flex items-center text-[#00A2B5] font-semibold text-sm group">
                      Read
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 md:py-28 bg-slate-50" data-testid="faq-section">
        <div className="container-custom">
          <SectionHeader
            overline="FAQs"
            title="Frequently Asked Questions"
            centered
          />

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-slate-200 px-6 overflow-hidden"
                  data-testid={`faq-${index}`}
                >
                  <AccordionTrigger className="py-6 text-left font-semibold text-slate-900 hover:no-underline hover:text-[#00A2B5] transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-slate-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mt-12"
          >
            <p className="text-slate-600 mb-4">Still have questions?</p>
            <Link
              to="/contact"
              className="btn-gradient inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold group"
              data-testid="faq-contact-cta"
            >
              Contact Our Team
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner variant="dark" />
    </main>
  );
};

export default ResourcesPage;
